import classNames from 'classnames';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { BarteringDocument, Listing, ProductTag, WithStringId } from './types';
import api from './api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAdd, faArrowCircleRight, faArrowLeft, faArrowRight, faCircle } from '@fortawesome/free-solid-svg-icons'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ImageBlur from './imageblur';

export const Home: FunctionComponent<{
    tags: Array<ProductTag>,
    roomTags: Array<ProductTag>,
    barters: Array<BarteringDocument>,
    readyToTrade: boolean,
    listing?: WithStringId<Listing>,
    onListing: (listing: WithStringId<Listing> | undefined) => void, refreshBarters: () => void
}> =
    ({ tags, roomTags, barters, listing, children, onListing, refreshBarters, readyToTrade }) => {
        const [searchParams, setSearchParams] = useSearchParams();
        const { id } = useParams();
        const [tag, setTag] = useState(undefined as string | undefined);
        const [loading, setLoading] = useState(false);
        const [pageSize, setPageSize] = useState(50);
        const [page, setPage] = useState(1);
        const [pages, setPages] = useState(1);

        const [listings, setListings] = useState([] as Array<WithStringId<Listing>>)

        useEffect(() => {
            setLoading(true);
            const request = tag ? api.getTagListings([tag], pageSize, page) : api.getLatestListings(pageSize, page)
            request
                .then(({ pages, listings }) => {
                    setLoading(false);
                    setPages(pages);
                    setListings(listings);
                    console.log('Listings:', listings);
                })
        }, [pageSize, page, tag]);
        useEffect(() => {
            if (id) {
                api.getListing(id)
                    .then(id => onListing(listing))
            }
        }, [id])
        const [pageSelector, pageSizeSelector] = PageSelector({ pages, page, pageSize, onPage: (page, size) => { setPage(page); setPageSize(size) }, alwaysShow: true });
        return <>
            <ProductListings listings={listings} loading={loading} onItemSelected={listing => onListing(listing)}>
                <MainMenu tags={roomTags} tag={tag} onTag={tag => setTag(tag)} />
                <div className='mb-3'>{pageSizeSelector}</div>
            </ProductListings>

            <div className='my-3'>{pageSelector}</div>
            {listing && <ListedView
                tags={tags}
                listing={listing}
                toggleTrading={trade => {
                    if (!readyToTrade) {
                        return alert('This account does not yet have a payment method or shipping address on file. Please go to your account settings to fix that.');
                    }
                    (trade ? api.markInterested(listing._id) : api.markUninterested(listing._id))
                        .then(() => refreshBarters())
                        .catch(err => alert(err));
                }}
                interested={!!barters.find(({ listingId }) => listingId === listing._id)} onClose={() => onListing(undefined)} />}
            {children}
        </>
    }

export const UserInventory: FunctionComponent<{
    listings: Array<WithStringId<Listing>>,
    onListing: (listing: WithStringId<Listing>) => void
}> = ({ listings, onListing }) => {
    const inventoryRef = useRef(undefined as any as HTMLDivElement);
    const [gapHeight, setGapHeight] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (inventoryRef) {
            setGapHeight(inventoryRef.current.offsetHeight);
        }
    }, [inventoryRef]);


    return <>
        <div className='inventory-gap' style={{ height: gapHeight + 10 + 'px' }} />
        <div className='inventory d-flex justify-content-between' ref={inventoryRef}>
            <div>
                {listings.map(listing => <div className='inventory-card m-2 cursor-pointer rounded' onClick={() => onListing(listing)}>
                    <img src={'/api/image?id=' + listing.images[0]} className='rounded'></img>
                </div>)}
            </div>
            <div>
                <div className='inventory-card inventory-card-add m-2' onClick={() => navigate('/list')}>
                    <FontAwesomeIcon icon={faAdd} />
                </div>
            </div>
        </div>
    </>
}
export const MainMenu: FunctionComponent<{ tags: Array<ProductTag>, tag: string | undefined, onTag: (tag: string | undefined) => void }> = ({ tags, tag, onTag }) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [left, setLeft] = useState(false);
    const [right, setRight] = useState(false)
    const containerRef = React.createRef<HTMLDivElement>();

    useEffect(() => {
        if (containerRef.current)
            handleScroll();
    }, [containerRef])

    const handleScroll = () => {
        const container = containerRef.current;
        if (!container) return;

        if (container.scrollLeft === 0) {
            setLeft(false);
        } else {
            setLeft(true);
        }
        if (container.scrollWidth - container.clientWidth === container.scrollLeft) {
            setRight(false);
        } else {
            setRight(true);
        }
        setScrollPosition(container.scrollLeft);
    };
    const goLeft = () => {
        const container = containerRef.current;
        if (!container) return;
        const newScrollPosition = Math.max(scrollPosition - container.clientWidth, 0);
        container.scrollTo({
            left: newScrollPosition,
            behavior: 'smooth'
        });
        setScrollPosition(newScrollPosition);
    };

    const goRight = () => {
        const container = containerRef.current;
        if (!container) return;
        const newScrollPosition = Math.min(scrollPosition + container.clientWidth, container.scrollWidth - container.clientWidth);
        container.scrollTo({
            left: newScrollPosition,
            behavior: 'smooth'
        });
        setScrollPosition(newScrollPosition);
    };

    return <div className="menu mb-2">
        <div
            className={'menu-item menu-item-nav cursor-pointer fs-2 ' + (left ? '' : 'menu-item-disabled')}
            onClick={goLeft}>
            <span className="fa-stack">
                <FontAwesomeIcon icon={faCircle} className='fa-stack-2x' />
                <FontAwesomeIcon icon={faArrowLeft} inverse className='fa-stack-1x' />
            </span>
        </div>
        <div className='flex-grow-1 menu-container' ref={containerRef} onScroll={handleScroll}>
            <div className='menu-item cursor-pointer fs-2' onClick={() => onTag(undefined)}>All</div>
            {tags.map(tag => <div className='menu-item cursor-pointer fs-2' onClick={() => onTag(tag.alias)} key={tag.alias}>{tag.title}</div>)}
        </div>
        <div
            className={'menu-item menu-item-nav fs-2  cursor-pointer ' + (right ? '' : 'menu-item-disabled')}
            onClick={goRight}>
            <span className="fa-stack">
                <FontAwesomeIcon icon={faCircle} className='fa-stack-2x' />
                <FontAwesomeIcon icon={faArrowRight} inverse className='fa-stack-1x' />
            </span>
        </div>
    </div>
}
export const PageSelector = ({
    pages,
    page,
    pageSize,
    alwaysShow = false,
    onPage
}: {
    pages: number,
    page: number,
    pageSize: number,
    alwaysShow: boolean,
    onPage: (page: number, pageSize: number) => void
}): JSX.Element[] => {
    const [showPageSize, setShowPageSize] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setShowPageSize(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return [<div className={'d-flex justify-content-center ' + (alwaysShow ? 'lock-bottom' : '')}>
        <ul className="pagination text-center">
            <li className={"page-item " + (page === 1 ? 'disabled' : '')}>
                <a className="page-link" href="#" tabIndex={-1} onClick={() => onPage(page - 1, pageSize)}>Previous</a>
            </li>
            {Array.from({ length: pages }, (_, i) => i + 1)
                .map(_page => <li className={'page-item ' + (page === _page ? 'active' : '')} key={_page}>
                    <a className='page-link' href="#" onClick={() => onPage(_page, pageSize)}>{_page}</a>
                </li>)}
            <li className={"page-item " + (page === pages ? 'disabled' : '')}>
                <a className="page-link" href="#" onClick={() => onPage(page + 1, pageSize)}>Next</a>
            </li>
        </ul></div>,
    <div className='d-flex justify-content-end'>
        <div className='dropdown' ref={menuRef}>
            <button className="btn btn-outline-secondary dropdown-toggle" type="button" onClick={() => setShowPageSize(!showPageSize)}>
                {pageSize}
            </button>
            <div className={'dropdown-menu dropdown-menu-right ' + (showPageSize ? 'show' : '')}>
                {[50, 100, 150].map(size => <div key={size} className={'dropdown-item ' + (pageSize === size ? 'active' : '')} onClick={() => {
                    onPage(1, size);
                    setShowPageSize(false);
                }}>{size}</div>)}
            </div>
        </div>
    </div>]
}
const ProductListings: FunctionComponent<{ listings: Array<WithStringId<Listing>>, loading: boolean, onItemSelected: (listing: WithStringId<Listing>) => void }> = ({ listings, loading, children, onItemSelected }) => {
    return <>
        {children}
        <div className='p-5' style={{ backgroundColor: '#E0EAEC' }}>
            {loading && <h2>Loading...</h2>}
            {!loading && listings.length === 0 ? <h2>No items listed under here. Be the first!</h2> : null}
            <div className='row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 gx-3 gy-3'>

                {listings.map(listing => <div className='col' key={listing._id}>
                    <div className='card cursor-pointer text-decoration-none listing-item border-0 rounded'
                        onClick={() => onItemSelected(listing)}>
                        <div className='justify-content-center'>
                            <ImageBlur src={'/api/image/primary?listingId=' + listing._id} />
                        </div>

                    </div>
                </div>)}
            </div>
        </div>
    </>
}

export const SearchPage: FunctionComponent<{
    onListing: (listing: WithStringId<Listing> | undefined) => void,
    listing?: WithStringId<Listing>
    barters: Array<WithStringId<BarteringDocument>>;
    tags: Array<ProductTag>
    readyToTrade: boolean,
    refreshBarters: () => void
}> = ({ onListing, listing, barters, tags, refreshBarters, readyToTrade }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [listings, setListings] = useState([] as Array<WithStringId<Listing>>)
    const [loading, setLoading] = useState(true);

    const query = searchParams.get('query') || '';

    useEffect(() => {
        setLoading(true);
        api.search(query)
            .then(listings => {
                setListings(listings)
                setLoading(false);
            })
    }, [query])

    return <div>
        <ProductListings listings={listings} loading={loading} onItemSelected={listing => onListing(listing)} />
        {listing && <ListedView
            tags={tags}
            listing={listing}
            toggleTrading={trade => {
                if (!readyToTrade) {
                    return alert('This account does not yet have a payment method or shipping address on file. Please go to your account settings to fix that.');
                }
                (trade ? api.markInterested(listing._id) : api.markUninterested(listing._id))
                    .then(() => refreshBarters())
                    .catch(err => alert(err));
            }}
            interested={!!barters.find(({ listingId, state }) => listingId === listing._id && state !== 'completed')} onClose={() => onListing(undefined)} />}
    </div>
}


export const ListedView: FunctionComponent<{
    tags: Array<ProductTag>,
    listing: WithStringId<Listing>,
    interested: boolean,
    toggleTrading: (trade: boolean) => void,
    onClose: () => void,
}> = ({ listing, interested, tags, toggleTrading, onClose }) => {
    const [currentImage, setCurrentImage] = useState(0);

    return <>
        <div className={'backdrop-blur'} />
        <div className={'backdrop-darken'} />
        <div className='front-drop d-flex justify-content-center p-5'>
            <div className='container'>
                <div className='row mb-2'>
                    <div className='col-md-1'>
                        <button className='btn btn-primary' onClick={() => onClose()}>Close</button>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-md-4'>
                        <div>
                            <div className='card p-2' style={{ backgroundColor: '#E0EAEC' }}>
                                <div className='card-title'>
                                    <div className='container'>
                                        <div className='row'>
                                            <div className='col'>
                                                Name: {listing.tagsAliases.map(alias => <span className='badge bg-secondary me-2'>{tags.find(tag => tag.alias === alias)?.title || ''}</span>)}
                                            </div>
                                            <div className='col'>
                                                Price: <strong>$ {listing.price}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <h5 className='card-title'>{listing.title}</h5>
                                </div>

                                <ImageBlur src={'/api/image?id=' + listing.images[currentImage]} />
                                <div className='mt-2'>
                                    <nav>
                                        <ul className="pagination justify-content-center">
                                            {listing.images.map((image, i) => <li className='page-item'>
                                                <a onClick={() => setCurrentImage(i)}
                                                    className={'cursor-pointer page-link me-1 ' + (i === currentImage ? 'text-primary' : 'text-secondary')}>
                                                    {i + 1}
                                                </a>
                                            </li>)}
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-8'>
                        <div className='card p-2' style={{ backgroundColor: '#E0EAEC' }}>
                            <div className="card-body">
                                <h5 className='card-title'>Product Details</h5>
                                <p>Description: {listing._id}</p>
                                <div >
                                    {listing.tagsAliases.map(alias => <span className='badge bg-secondary me-2'>{tags.find(tag => tag.alias === alias)?.title || ''}</span>)}
                                </div>
                                <div className='text-center mt-3'>
                                    <button className={classNames('btn', interested ? 'btn-light' : 'btn-primary', 'btn-lg')} onClick={() => toggleTrading(!interested)}>
                                        <strong>{interested ? "Cancel trading" : "Start trading"}</strong>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}